import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setActiveTenantSlug, getActiveTenantSlug } from "@/lib/api";

const TenantContext = createContext(null);

/**
 * Apply a tenant's theme_config to CSS custom properties on <html>.
 * Every downstream Tailwind class that references `var(--brand-primary)` etc.
 * inherits automatically.
 */
function applyTheme(theme) {
  if (!theme || typeof document === "undefined") return;
  const root = document.documentElement;
  const map = {
    "--brand-primary": theme.brand_primary,
    "--brand-secondary": theme.brand_secondary,
    "--brand-accent": theme.brand_accent,
    "--brand-ink": theme.brand_ink,
    "--brand-muted": theme.brand_muted,
    "--font-headline": theme.hero_headline_font,
    "--font-body": theme.body_font,
  };
  Object.entries(map).forEach(([k, v]) => {
    if (v) root.style.setProperty(k, v);
  });
  if (theme.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = theme.favicon_url;
  }
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(getActiveTenantSlug());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public/tenant/current");
      setTenant(data);
      applyTheme(data.theme_config);
      if (data.name) document.title = data.name;
    } catch (e) {
      // Non-fatal — public endpoint should always resolve
      console.warn("Tenant resolve failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const switchTenant = useCallback(async (slug) => {
    setActiveTenantSlug(slug);
    setActiveSlug(slug || "");
    setLoading(true);
    await load();
  }, [load]);

  return (
    <TenantContext.Provider value={{ tenant, loading, activeSlug, switchTenant, reload: load }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext) || { tenant: null, loading: true, activeSlug: "", switchTenant: () => {}, reload: () => {} };
}
